<template>
    <div class="modal">
        <div class="modal-container">
            <div class="modal-header">
                <close-icon v-on:click="closeView"></close-icon>
                <h1>Manage Subscription</h1>
            </div>

            <div class="modal-body no-pad">
                <div class="m-4 py-2 text-center bg-yellow-lighter text-yellow-darker" v-if="subscriptionIsPaused">
                    Subscription is paused until {{ moment(this.originalSubscription.paused_until).format('DD/MM/YYYY') }}
                </div>
                <div class="flex h-full">
                    <div class="w-full md:w-1/2 md:pr-4">
                        <div class="account-body">
                            <h2>Package Details</h2>

                            <div class="form-field">
                                <label>Subscription Package</label>
                                <select v-model="selectedPackage">
                                    <option :value="null">
                                        No Subscription (PAYG)
                                    </option>
                                    <optgroup label="Current">
                                        <option v-for="option in packageOptions.filter(pkg => pkg.current)" :value="option.id">
                                            {{ option.label }}
                                        </option>
                                    </optgroup>
                                    <optgroup label="Old">
                                        <option v-for="option in packageOptions.filter(pkg => !pkg.current)" :value="option.id">
                                            {{ option.label }}
                                        </option>
                                    </optgroup>
                                </select>
                                <p class="enablements" v-if="!packageChanged">
                                    <a href="#" @click.prevent="loadEnablements" >Manage Enablements</a>
                                </p>
                            </div>

                            <template v-if="paymentMethod !== 'free' && paymentMethod !== 'switch'">
                                <div class="form-field" v-if="packageChanged && canChangePackageDuration">
                                    <label>Initial Period</label>
                                    <input type="text" v-model="packageDuration" class="form-field inline mr-2" size="3"> months for £<input type="text" v-model="subscriptionCost" :readonly="selectedPromotion" class="form-field inline" size="8"> total
                                </div>

                                <div class="form-field" v-if="packageChanged && canChangePackageDuration">
                                    <label>Then recurring every</label>
                                    <input type="text" v-model="recurringIntervals" class="form-field inline mr-2" size="3"> months for £<input type="text" v-model="recurringSubscriptionCost" :readonly="selectedPromotion" class="form-field inline" size="8"> total
                                </div>
                            </template>

                            <div class="form-field" v-if="selectedPackageMinTerm > 1 && packageChanged">
                                <label>Cancellable Date</label>
                                <p class="text-xs mb-2">The selected package has a minimum term, and therefore cannot be cancelled before the term has lapsed. This date can be adjusted after payment.</p>

                                <div class="flex items-center">
                                    <simple-date-time-picker
                                        v-model="cancellableAfter" disabled>
                                    </simple-date-time-picker>
                                </div>
                            </div>

                            <div class="form-field" v-if="selectedPackagePromotionable && paymentMethod !== 'switch'">
                                <label>Apply Promotion</label>
                                <select v-model="selectedPromotion" :disabled="!packageChanged">
                                    <option :value="null">No Promotion</option>
                                    <option v-for="option in selectablePromotions" :value="option.id">
                                        {{ option.label }}
                                    </option>
                                </select>
                            </div>

                            <div class="form-field" v-if="promotionChanged && selectedPromotion">
                                <label>Promotion Duration</label>
                                <input type="text" v-model="promotionDuration" readonly="readonly" class="form-field inline">
                            </div>

                            <div class="form-field" v-if="!packageChanged && originalSubscription">
                                <label>Recurring Cost (&pound;)</label>
                                <input type="text" :value="subscriptionRenewalPrice" class="form-field inline" readonly="readonly">
                            </div>

                            <div class="form-field" v-if="packageChanged">
                                <label>Payment Method</label>
                                <select v-model="paymentMethod">
                                    <option value="card">Credit Card</option>
                                    <option value="invoice">Invoice</option>
                                    <option value="user-checkout">User Checkout</option>
                                    <option value="switch">Switch at Renewal</option>
                                    <option value="free" v-if="!selectedPromotion">Free</option>
                                </select>
                            </div>

                            <button
                                class="btn-primary mt-4"
                                v-if="selectedPackage && !packageChanged"
                                @click="extendingPackage = true"
                            >Extend Subscription</button>

                            <div class="form-field" v-if="packageChanged && paymentMethod !== 'switch'">
                                <label>VAT Rate</label>
                                <TaxRateSelect v-model="taxRate" />
                            </div>

                            <div class="mt-4">
                                <p class="note text-red" v-if="selectedPromotion && packageChanged">
                                    When applying a promotion, you cannot amend the subscription charge
                                </p>

                                <p class="note" v-if="packageChanged && !extendingPackage && paymentMethod !== 'switch'">
                                    By changing the package a new subscription will be created immediately, superceding the existing one.<br/>
                                </p>
                            </div>
                        </div>
                    </div>

                    <div class="w-full md:w-1/2 md:pl-4" v-if="shouldShowSaveButton">

                        <div class="account-body" v-if="originalSubscription">
                            <h3>Subscription Details</h3>

                            <div class="form-field" v-if="originalSubscription">
                                <label>Expiry</label>
                                <simple-date-time-picker
                                    v-model="originalSubscription.expires">
                                </simple-date-time-picker>
                            </div>

                            <div class="form-field" v-if="originalSubscription">
                                <label>Auto Renew</label>
                                <CheckBox v-model="originalSubscription.auto_renew" val="true" />
                            </div>

                            <div class="form-field" v-if="originalSubscription && originalSubscription.cancellable_after > originalSubscription.expires">
                                <label>Cancellable Date</label>
                                <p class="text-xs mb-2">The selected package has a minimum term, and therefore cannot be cancelled before the term has lapsed. At your discretion, you may amend the date.</p>

                                <div class="flex items-center">
                                    <simple-date-time-picker
                                        v-model="originalSubscription.cancellable_after">
                                    </simple-date-time-picker>
                                </div>
                            </div>

                            <div class="form-field" v-if="originalSubscription">
                                <label>Pause</label>
                                <router-link
                                    v-if="!subscriptionIsPaused"
                                    :to="{
                                        name: 'subscriptions.pause',
                                        params: {
                                            subscriptionId: this.originalSubscription.id,
                                            subscription: originalSubscription
                                        }
                                }">Pause Subscription</router-link>
                                <a
                                    href="#"
                                    v-if="subscriptionIsPaused"
                                    @click.prevent="unpauseSubscription"
                                >Unpause Subscription</a>
                            </div>
                        </div>
                    </div>

                    <div class="w-full md:w-1/2 md:pl-4" v-else>
                        <div class="account-body" v-if="paymentMethod !== 'switch'">
                            <h2>Options</h2>

                            <div class="form-field" v-if="packageChanged">
                                <label>Process as a Renewal</label>
                                <CheckBox v-model="renewalOrder" :disabled="!originalSubscription" />
                            </div>

                            <div class="form-field" v-if="packageChanged">
                                <label>Extend current expiry date</label>
                                <CheckBox v-model="extendCurrentExpiry" :disabled="!originalSubscription" />
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <div class="modal-footer">
                <div class="flex">
                    <div class="left text-left">
                        <a href="javascript:void(0)" @click.prevent="changingAttribution = true" style="line-height: 42px;" v-if="packageChanged && !changingAttribution">Sold by: {{ currentUserEmail }}</a>
                        <select v-model="selectedAttribution" v-if="packageChanged && changingAttribution">
                            <option v-for="(user, id) in users" :value="user.id">{{ user.email }}</option>
                        </select>
                    </div>

                    <div class="middle text-middle">

                    </div>

                    <div class="right text-right">
                        <button class="btn-tertiary muted" type="button" @click="closeView">Close</button>

                        <button
                            class="btn-primary"
                            type="button"
                            :disabled="!promotionMatchesPackage"
                            @click="switchPackage"
                            v-if="shouldShowChangePackageButton"
                        >Change Package</button>

                        <card-payment
                            :amount="parseFloat(subscriptionCost)"
                            :disabled="!promotionMatchesPackage"
                            :saved="savedBilling"
                            :stripeKey="stripeKey"
                            text="Process Payment"
                            title="Card Payment"
                            @submit="handlePayment"
                            v-if="shouldShowCardPaymentButton"
                        ></card-payment>

                        <invoice-payment
                            :amount="parseFloat(subscriptionCost)"
                            :disabled="!promotionMatchesPackage"
                            :account="account"
                            text="Create Invoice"
                            title="Invoice Payment"
                            @submit="handleInvoicePayment"
                            v-if="shouldShowInvoicePaymentButton"
                        ></invoice-payment>

                        <button
                            class="btn-primary"
                            type="button"
                            @click="saveForm"
                            v-if="shouldShowSaveButton"
                        >Save</button>
                    </div>
                </div>
            </div>

            <router-view @closing="loadData" v-slot="{ Component }">
                <transition name="drop-in" mode="in-out">
                    <component :is="Component"></component>
                </transition>
            </router-view>
        </div>
    </div>
</template>

<script>
    import CardPayment from '../../CardPayment'
    import InvoicePayment from '../../InvoicePayment'
    import TaxRateSelect from '../../TaxRateSelect'
    import moment from "moment"

    export default {
        name: 'SubscriptionView',
        components: {
            CardPayment,
            InvoicePayment,
            TaxRateSelect,
        },
        data() {
            return {
                users: null,
                account: null,
                packages: null,
                providers: null,
                promotions: null,
                paymentToken: null,
                packageDuration: 1,
                recurringIntervals: 1,
                subscriptionCost: 0,
                takingPayment: false,
                paymentMethod: 'card',
                selectedPackage: null,
                cancellableAfter: null,
                selectedPromotion: null,
                selectedAttribution: null,
                selectedPackageMinTerm: 1,
                changingAttribution: false,
                originalSubscription: null,
                preselectedPromotion: null,
                preselectedPackage: null,
                preselectedPackageCost: null,
                recurringSubscriptionCost: 0,
                taxRate: null,
                extendingPackage: false,
                renewalOrder: false,
                extendCurrentExpiry: false,
            }
        },
        mounted() {
            this.loadData()

            this.selectedAttribution = window.Laravel.auth.user.id

            this.setMetaTitle('View Subscription | JournoLink Control')
        },
        beforeDestroy() {
            //
        },
        watch: {
            selectedPackage(packageId) {
                this.subscriptionCost = 0
                this.recurringSubscriptionCost = 0
                this.extendingPackage = false

                if (this.newPackage) {
                    this.packageDuration = this.newPackage.minimum_term > this.newPackage.interval ? this.newPackage.minimum_term : this.newPackage.interval
                    this.recurringIntervals = this.newPackage.interval
                    this.subscriptionCost = this.calculateSubscriptionCost(this.newSubscriptionCharge, this.packageDuration)
                    this.recurringSubscriptionCost = this.calculateSubscriptionCost(this.newSubscriptionCharge, this.recurringIntervals)
                    this.selectedPackageMinTerm = this.newPackage.minimum_term ?? 1
                }

                if (!this.promotionMatchesPackage) {
                    this.selectedPromotion = null
                }
            },
            selectedPromotion(promotionId) {
                this.subscriptionCost = 0

                if (this.newPackage) {
                    this.subscriptionCost = this.newSubscriptionCharge
                    this.recurringSubscriptionCost = this.newSubscriptionCharge
                }

                if (null !== promotionId) {
                    this.paymentMethod = 'card'
                }
            },
            packageDuration(newValue) {
                this.subscriptionCost = this.calculateSubscriptionCost(this.newSubscriptionCharge, newValue)
            },
            recurringIntervals(newValue) {
                this.recurringSubscriptionCost = this.calculateSubscriptionCost(this.newSubscriptionCharge, newValue)
            },
            selectedPackageMinTerm(term) {
                this.cancellableAfter = moment().add(term * this.newPackage.interval, 'M').toISOString()
            },
            taxRate(newValue, oldValue) {
                this.subscriptionCost = this.calculateSubscriptionCost(this.newSubscriptionCharge, this.packageDuration)
                this.recurringSubscriptionCost = this.calculateSubscriptionCost(this.newSubscriptionCharge, this.recurringIntervals)
            },
            extendingPackage(newValue) {
                if (newValue) {
                    this.renewalOrder = true
                    this.packageDuration = 1
                    this.recurringIntervals = 1
                    this.subscriptionCost = this.calculateSubscriptionCost(this.newSubscriptionCharge, this.packageDuration)
                    this.recurringSubscriptionCost = this.calculateSubscriptionCost(this.newSubscriptionCharge, this.recurringIntervals)
                    this.selectedPackageMinTerm = 1

                    return
                }

                this.renewalOrder = false
            }
        },
        computed: {
            /**
             * Return the options arary for the available packages
             *
             * @return {Array}
             */
            packageOptions () {
                let options = []

                if (!this.packages) {
                    return options
                }

                for (let index in this.packages) {
                    if (!this.packages.hasOwnProperty(index)) {
                        continue
                    }

                    options.push({
                        id: this.packages[index].id,
                        label: this.packages[index].name + (this.originalPackage ? (this.packages[index].id === this.originalPackage.id ? ' (Current)' : '') : ''),
                        current: this.packages[index].current,
                    })
                }

                return options
            },
            /**
             * Return the options array for the promotion selection
             *
             * @return {Array}
             */
            promotionOptions () {
                let options = [];

                if (!this.promotions) {
                    return options
                }

                for (let index in this.promotions) {
                    if (!this.promotions.hasOwnProperty(index)) {
                        continue
                    }

                    options.push({
                        id: this.promotions[index].id,
                        label: this.promotions[index].code,
                        discount: {
                            percentage: this.promotions[index].percentage,
                            fixed_amount: this.promotions[index].fixed_amount,
                            duration: this.promotions[index].duration,
                        },
                        package: this.promotions[index].orderable_id,
                    })
                }

                return options
            },
            /**
             * Return the new package
             *
             * @return {Object}
             */
            newPackage () {
                if (!this.selectedPackage) {
                    return null
                }

                if (!this.packageChanged) {
                    return null
                }

                return this.packages.find((item) => {
                    return item.id === this.selectedPackage
                })
            },
            /**
             * Return the active package, whether changed or not
             *
             * @return {Object}
             */
            activePackage () {
                if (!this.selectedPackage) {
                    return null
                }

                return this.packages.filter((item) => {
                    return item.id === this.selectedPackage
                }).shift()
            },
            /**
             * Return the active promotion, whether changed or not
             *
             * @return {Object}
             */
            activePromotion () {
                if (!this.selectedPromotion) {
                    return null
                }

                return this.promotions.filter((item) => {
                    return item.id === this.selectedPromotion
                }).shift()
            },
            /**
             * Return the recurring charge for the new selected package
             *
             * @return {Number}
             */
            newPackageRecurringCharge() {
                if (!this.activePackage) {
                    return null
                }

                return parseFloat(this.newSubscriptionCharge).toFixed(2)

                return null
            },
            /**
             * Detemine if the selected subscription has been changed
             *
             * @return {Boolean}
             */
            packageChanged() {
                if (this.extendingPackage) {
                    return true
                }

                if (!this.selectedPackage) {
                    return false
                }

                if (!this.originalSubscription) {
                    return true
                }

                return this.selectedPackage !== this.originalSubscription.package_id
            },
            /**
             * Detemine if the selected promotion has been changed
             *
             * @return {Boolean}
             */
            promotionChanged() {
                if (this.originalPromotion && !this.selectedPromotion) {
                    return true
                }

                if (this.selectedPromotion && !this.originalPromotion) {
                    return true
                }

                if (!this.originalPromotion) {
                    return false
                }

                return this.selectedPromotion !== this.originalPromotion.promotion_id
            },
            /**
             * Return the original package, pre-amendment
             *
             * @return {Object}
             */
            originalPackage() {
                if (this.originalSubscription) {
                    return this.originalSubscription.package
                }

                return null
            },
            /**
             * Return the original promotion, pre-amendment
             *
             * @return {Object}
             */
            originalPromotion() {
                if (!this.originalSubscription || !this.originalSubscription.order || !this.originalSubscription.order.promotion) {
                    return null
                }

                return this.originalSubscription.order.promotion
            },
            /**
             * Determine if the currently selected package is able to have a promotion attached
             *
             * @return {Boolean}
             */
            selectedPackagePromotionable() {
                return this.selectedPackage !== null
            },
            /**
             * Return the currently selectable promotions based on the selected package
             *
             * @return {Array}
             */
            selectablePromotions() {
                return this.promotionOptions.filter((item) => {
                    return item.package === this.selectedPackage
                })
            },
            /**
             * Determine if the currently selected promotion can be applied to the currently
             * selected package
             *
             * @return {Boolean}
             */
            promotionMatchesPackage() {
                if (!this.selectedPromotion || !this.selectedPackage) {
                    return true
                }

                return this.selectablePromotions.filter((item) => {
                    return item.id === this.selectedPromotion
                }).length > 0
            },
            /**
             * Return the saved billing agreements
             *
             * @return {Array}
             */
            savedBilling() {
                if (!this.originalSubscription || !this.originalSubscription.order || !this.originalSubscription.order.order_billing) {
                    return []
                }

                if (this.originalSubscription.order.order_billing.recurring_token === null) {
                    return []
                }

                return [this.originalSubscription.order.order_billing]
            },
            /**
             * Determine if the original Subscription contains an active billing agreement
             *
             * @return {Boolean}
             */
            hasActiveBillingAgreement() {
                if (!this.originalSubscription || !this.originalSubscription.order || !this.originalSubscription.order.order_billing) {
                    return false
                }

                if (this.originalSubscription.order.order_billing.recurring_token !== null) {
                    return true
                }

                return false
            },
            /**
             * Determine if the "Process Card Payment" button should be shown
             *
             * @return {Boolean}
             */
            shouldShowCardPaymentButton() {
                return (this.packageChanged) && this.subscriptionCost > 0 && this.paymentMethod === 'card'
            },
            /**
             * Determine if the "Process Invoice Payment" button should be shown
             *
             * @return {Boolean}
             */
            shouldShowInvoicePaymentButton() {
                return (this.packageChanged) && this.subscriptionCost > 0 && this.paymentMethod === 'invoice'
            },
            /**
             * Determine if the "Change Package" button should be shown
             *
             * @return {Boolean}
             */
            shouldShowChangePackageButton() {
                return (!this.packageChanged && this.promotionChanged) || (this.packageChanged && (this.subscriptionCost == 0 || (this.paymentMethod !== 'card' && this.paymentMethod !== 'invoice')))
            },
            /**
             * Determine if the "Save" button should be shown
             *
             * @return {Boolean}
             */
            shouldShowSaveButton() {
                return (!this.packageChanged && !this.promotionChanged)
            },
            /**
             * Determine if the "Recurring Charge" field should be shown
             *
             * @return {Boolean}
             */
            shouldShowRecurringCharge() {
                return (this.packageChanged || this.promotionChanged) && this.activePackage
            },
            /**
             * The charge applicable to the new subscription
             *
             * @return {String|Number}
             */
            newSubscriptionCharge() {
                //let intervalGrossCost = this.activePackage ? this.activePackage.gross_cost / this.activePackage.interval : 0
                let intervalNetCost = this.activePackage ? this.activePackage.cost / this.activePackage.interval : 0

                if (null === this.selectedPackage) {
                    return null
                }

                if (null === this.selectedPromotion) {
                    return this.activePackage ? (intervalNetCost * (1 + this.taxRate.rate)).toFixed(2) : null
                }

                if (null !== this.activePromotion.percentage) {
                    return (parseFloat(intervalNetCost * (1 + this.taxRate.rate)) - (parseFloat(intervalNetCost * (1 + this.taxRate.rate)) * parseFloat(this.activePromotion.percentage))).toFixed(2)
                }

                if (null !== this.activePromotion.fixed_amount) {
                    return (parseFloat(intervalNetCost * (1 + this.taxRate.rate)) - parseFloat(this.activePromotion.fixed_amount)).toFixed(2)
                }

                return (intervalNetCost * (1 + this.taxRate.rate)).toFixed(2)
            },
            /**
             * The overall duration of the selected promotion
             *
             * @return {String}
             */
            promotionDuration() {
                if (!this.selectedPackage || !this.selectedPromotion) {
                    return null
                }

                if (!this.activePromotion) {
                    return null
                }

                if (this.activePromotion.duration === -1) {
                    return 'No expiry'
                }

                return this.activePromotion.duration + ' ' + this.activePackage.period_text + (this.activePromotion.duration > 1 ? 's' : '')
            },
            /**
             * The current authenticated user email address
             *
             * @return {String}
             */
            currentUserEmail() {
                return window.Laravel.auth.user.email
            },
            /**
             * Retrieve the currently renewal price for the next billing
             *
             * @return {Number}
             */
            subscriptionRenewalPrice() {
                if (!this.originalSubscription || !this.originalSubscription.order) {
                    return 0.00;
                }

                return this.originalSubscription.order.recurring_gross_cost.toFixed(2)
            },
            /**
             * Determine if the subscription is currently paused
             *
             * @return {Boolean}
             */
            subscriptionIsPaused() {
                return this.originalSubscription &&
                    this.originalSubscription.paused_until !== null &&
                    moment(this.originalSubscription.paused_until).isAfter(moment())
            },
            /**
             * Determine if the duration field can be changed.
             *
             * @return {Boolean}
             */
            canChangePackageDuration() {
                return true
            },
        },
        methods: {
            loadData() {
                this.stripeKey = document.querySelector('meta[name="stripe-key"]').getAttribute('content')

                axios.get(route('subscriptions.show', this.$route.params.accountId))
                    .then(response => {
                        this.originalSubscription = response.data.subscription
                        this.extendCurrentExpiry = this.originalSubscription !== null

                        this.selectedPackage = null

                        if (this.originalSubscription !== null) {
                            this.selectedPackage = this.originalSubscription.package.id
                        }

                        this.packages = response.data.packages
                        this.promotions = response.data.promotions
                        this.providers = response.data.providers
                        this.users = response.data.users
                        this.account = response.data.account
                        this.taxRate = this.getDefaultTaxRate()

                        if (this.originalSubscription && this.originalSubscription.order && this.originalSubscription.order.promotion) {
                            this.selectedPromotion = this.originalSubscription.order.promotion.promotion_id
                        }

                        this.handlePreselections(this.account.flags)

                        this.setMetaTitle('Subscription Management | JournoLink Control')
                    })
                    .catch(e => {
                        console.error({e})
                    })
            },
            /**
             * Handle a request to change the current active package
             *
             * @return {void}
             */
            switchPackage() {
                axios.post(
                    route('subscriptions.change', this.$route.params.accountId), {
                        package_id: this.selectedPackage ? this.selectedPackage : 0,
                        cost: this.subscriptionCost,
                        recurring_cost: this.recurringSubscriptionCost,
                        payment_method: this.paymentMethod,
                        payment_token: this.paymentToken,
                        promotion_id: this.selectedPromotion ? this.selectedPromotion : 0,
                        attribution_id: this.selectedAttribution,
                        intervals: this.packageDuration,
                        recurring_intervals: this.recurringIntervals,
                        tax_rate: this.taxRate.rate,
                        renewal: this.renewalOrder,
                        extend: this.extendCurrentExpiry,
                    })
                    .then(response => {
                        if (response.data.subscription) {
                            this.originalSubscription = response.data.subscription
                            this.selectedPackage = response.data.subscription.package_id
                            this.subscriptionCost = null
                            this.extendingPackage = false
                        }

                        this.handlePreselections(response.data.flags);

                        this.$bus.$emit('account.reload')
                        this.$bus.$emit('alert.success', 'Account subscription has been updated')
                        this.setMetaTitle('Subscription Management | JournoLink Control')
                    })
                    .catch(e => {
                        // Did we receive a validation response?
                        if (e.response && e.response.status === 422) {
                            if (e.response.data.errors.paymentToken) {
                                let error = e.response.data.errors.paymentToken.shift()
                                this.$bus.$emit('alert.danger', error)

                                return
                            }
                        }

                        this.$bus.$emit('alert.danger', 'There was an error in processing the subscription change')
                    });
            },
            saveForm() {
                if (this.originalSubscription) {
                    this.originalSubscription.expires = moment(this.originalSubscription.expires).format('YYYY-MM-DD HH:mm:ss')

                    axios.post(route('subscriptions.save', this.$route.params.accountId), this.originalSubscription)
                        .then(response => {
                            this.originalSubscription = response.data.subscription;
                            this.setMetaTitle('Subscription Management | JournoLink Control')
                            this.$bus.$emit('alert.success', 'Account subscription has been updated')
                        })
                        .catch(e => {
                            console.error({e})
                        });
                }
            },
            /**
             * Perform the request to unpause the subscription
             *
             * @return {void}
             */
            unpauseSubscription() {
                axios.post(route('subscriptions.unpause', this.$route.params.accountId))
                    .then(response => {
                        this.originalSubscription = response.data.subscription
                        this.setMetaTitle('Subscription Management | JournoLink Control')
                        this.$bus.$emit('alert.success', 'Account subscription has been updated')
                    })
                    .catch(e => {
                        console.error({e})
                    })
            },
            /**
             * Remove Preselected Package
             */
            removePreselectedPackage() {
                this.alert.fire({
                    title: 'Are you sure?',
                    text: 'This will remove the preselected package from this account',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, continue',
                    confirmButtonColor: '#E3342F',
                    cancelButtonText: 'No, go back'
                })
                .then((selected) => {
                    if(selected.value) {
                        this.preselectedPackage = null
                        axios.delete(route('subscriptions.preselection.package', this.$route.params.accountId))
                        .then(response => {
                            this.$bus.$emit('alert.success', 'Package Preselection Has Been Removed')
                        })
                        .catch(e => {
                            console.error({e})
                        });
                    }
                })
            },
            /**
             * Remove Preselected Promotion
             */
            removePreselectedPromotion() {
                this.alert.fire({
                    title: 'Are you sure?',
                    text: 'This will remove the preselected promotion from this account',
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonText: 'Yes, continue',
                    confirmButtonColor: '#E3342F',
                    cancelButtonText: 'No, go back'
                })
                .then((selected) => {
                    if(selected.value) {
                        this.preselectedPromotion = null
                        axios.delete(route('subscriptions.preselection.promotion', this.$route.params.accountId))
                        .then(response => {
                            this.$bus.$emit('alert.success', 'Promotion Preselection Has Been Removed')
                        })
                        .catch(e => {
                            console.error({e})
                        });
                    }
                })
            },
            /**
             * Emit the close event to the parent
             *
             * @return {void}
             */
            closeView() {
                this.$root.goBack()
            },
            /**
             * Handle a payment event
             *
             * @param  {String} token
             * @return {void}
             */
            handlePayment(token) {
                this.paymentToken = token.id

                this.switchPackage()
            },
            /**
             * Handle an invoice payment event
             *
             * @param {String} token
             * @return {void}
             */
            handleInvoicePayment(token) {
                this.paymentToken = token

                this.switchPackage()
            },
            /**
             * Handles Preselected Packages and Promotions
             *
             * @param {array} flags
             * @return {void}
             */
            handlePreselections(flags) {
                this.preselectedPackage = null
                this.preselectedPackageCost = null
                this.preselectedPromotion = null

                if (flags && flags.hasOwnProperty('user_checkout')) {
                    let packageFlag = flags.user_checkout
                    if (typeof JSON.parse(packageFlag) === 'object') {
                        let packageObject = JSON.parse(packageFlag)
                        packageFlag = packageObject.package
                        this.preselectedPackageCost = packageObject.cost
                        this.preselectedPromotion = this.promotions[this.promotions.findIndex(p => p.id == packageObject.promotion)]
                    }

                    this.preselectedPackage = this.packages[this.packages.findIndex(p => p.id == packageFlag)]
                }

                if (flags && flags.hasOwnProperty('promotion_selected')) {
                    this.preselectedPromotion = this.promotions[this.promotions.findIndex(p => p.id == flags.promotion_selected)]
                }
            },
            /**
             * Handle an [Escape] keypress
             *
             * @return {void}
             */
            onEscapeKeypress() {
                this.closeView()
            },
            /**
             * Load enablements
             * @return {void}
             */
            loadEnablements() {
                this.$router.push({ name: 'subscriptions.enablements', params: { subscriptionId: this.originalSubscription.id } })
            },

            calculateSubscriptionCost(charge, duration) {
                return (parseFloat(charge) * duration).toFixed(2);
            },

            /**
             * Return the default tax code for this Order.
             *
             * @return
             */
            getDefaultTaxRate() {
                return this.account.tax_rate ?
                    this.$store.state.taxRates.find(el => el.rate === this.account.tax_rate.tax_rate) :
                    this.$store.state.defaultTaxRate
            },
        }
    }
</script>

<style lang="less">
    .vdp-datepicker {
        display: inline-block;
        border: 1px solid #dddddd;
        padding: 5px;
    }

    .modal-body {
        h2, h3 {
            text-transform: uppercase;
            font-size: 1.2em;
            letter-spacing: .08em;
            color: #34495e;
            margin-bottom: 10px;
        }
    }

    p.note {
        margin: 10px 0;
        font-weight: bold;
    }

    p.enablements {
        padding: 0.5rem 0;
    }

    .preselections {
        a {
            text-decoration: underline;
            cursor: pointer;
        }
    }
</style>
